/* Kudsi email popup: behaviour.
   No dependencies. Works two ways:
   1. Shopify theme (default): the form posts natively to Shopify's customer form. The page reloads, the Liquid
      snippet marks the result, and this script reopens the popup on the success view.
   2. Async: pass a `submit(email)` function that returns a Promise. Resolve to show the success view,
      reject (optionally with an Error message) to show an error under the field.

   Usage: KudsiPopup.init({ ...options })   See DEFAULTS below. */
(function () {
  'use strict';

  var DEFAULTS = {
    rootId: 'kudsi-popup',
    delayMs: 6000,            // wait before the popup opens on a visit
    dismissDays: 14,          // after "No thanks" / close, stay away this long
    subscribedDays: 365,      // after a signup, stay away this long
    storageKey: 'kudsi_popup_v1',
    submit: null,             // async function (email) {}  — omit for native Shopify form post
    onOpen: null,             // function (state) {}        — analytics hooks
    onClose: null,            // function (reason) {}
    onSubscribe: null,        // function (email) {}
    onCopy: null,             // function (code) {}
    copy: {
      invalid: 'Please enter a valid email address.',
      failed: 'Something went wrong. Please try again.',
      sending: 'Sending…',
      copied: 'Copied',
      copiedLive: 'Code copied to your clipboard.'
    }
  };

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), [tabindex]:not([tabindex="-1"])';

  function readStore(key) {
    try { return JSON.parse(window.localStorage.getItem(key) || 'null'); } catch (e) { return null; }
  }
  function writeStore(key, value) {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* private mode: carry on */ }
  }

  function init(options) {
    var cfg = Object.assign({}, DEFAULTS, options || {});
    cfg.copy = Object.assign({}, DEFAULTS.copy, (options && options.copy) || {});

    var root = document.getElementById(cfg.rootId);
    if (!root) return null;

    var dialog = root.querySelector('.kp__dialog');
    var views = {
      signup: root.querySelector('[data-kp-view="signup"]'),
      success: root.querySelector('[data-kp-view="success"]')
    };
    var form = root.querySelector('.kp__form');
    var input = root.querySelector('.kp__input');
    var errorEl = root.querySelector('.kp__error');
    var submitBtn = form.querySelector('[type="submit"]');
    var submitLabel = submitBtn.textContent;
    var copyBtn = root.querySelector('[data-kp-copy]');
    var copyLabel = copyBtn ? copyBtn.textContent : '';
    var codeEl = root.querySelector('[data-kp-code]');
    var live = root.querySelector('[data-kp-live]');
    var lastFocus = null;
    var openTimer = null;
    var isOpen = false;

    // Images are loading="lazy" so they cost nothing on visits where the popup never opens.
    // Fetch a view's images just before it is needed so they are there when it appears.
    function preload(view) {
      Array.prototype.forEach.call(views[view].querySelectorAll('img'), function (img) {
        var src = img.getAttribute('src');
        if (src) { var probe = new Image(); probe.src = src; }
      });
    }

    function setState(state) {
      root.setAttribute('data-state', state);
      views.signup.hidden = state !== 'signup';
      views.success.hidden = state !== 'success';
      dialog.setAttribute('aria-labelledby', state === 'success' ? 'kp-title-success' : 'kp-title-signup');
    }

    function showError(message) {
      errorEl.textContent = message;
      errorEl.hidden = false;
      input.setAttribute('aria-invalid', 'true');
    }
    function clearError() {
      errorEl.textContent = '';
      errorEl.hidden = true;
      input.removeAttribute('aria-invalid');
    }
    function setLoading(on) {
      submitBtn.disabled = on;
      submitBtn.textContent = on ? cfg.copy.sending : submitLabel;
    }

    function open(state) {
      if (isOpen) { setState(state || 'signup'); return; }
      window.clearTimeout(openTimer);
      lastFocus = document.activeElement;
      setState(state || 'signup');
      preload('success');
      root.hidden = false;
      document.documentElement.classList.add('kp-lock');
      // Force a frame so the opening transition runs.
      void root.offsetWidth;
      root.classList.add('is-open');
      isOpen = true;
      document.addEventListener('keydown', onKeydown, true);
      focusInitial();
      if (typeof cfg.onOpen === 'function') cfg.onOpen(root.getAttribute('data-state'));
    }

    function focusInitial() {
      var state = root.getAttribute('data-state');
      var finePointer = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;
      if (state === 'success') {
        var title = root.querySelector('#kp-title-success');
        (title || dialog).focus({ preventScroll: true });
      } else if (finePointer) {
        // Desktop: go straight to the field. On touch devices this would pop the keyboard over the offer.
        input.focus({ preventScroll: true });
      } else {
        dialog.focus({ preventScroll: true });
      }
    }

    function close(reason) {
      if (!isOpen) return;
      isOpen = false;
      root.classList.remove('is-open');
      document.documentElement.classList.remove('kp-lock');
      document.removeEventListener('keydown', onKeydown, true);
      var done = function () { root.hidden = true; };
      var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) done(); else window.setTimeout(done, 260);
      if (root.getAttribute('data-state') !== 'success') {
        var prior = readStore(cfg.storageKey);
        if (!prior || prior.status !== 'subscribed') writeStore(cfg.storageKey, { status: 'dismissed', at: Date.now() });
      }
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus({ preventScroll: true });
      if (typeof cfg.onClose === 'function') cfg.onClose(reason || 'close');
    }

    function onKeydown(event) {
      if (event.key === 'Escape') { event.preventDefault(); close('escape'); return; }
      if (event.key !== 'Tab') return;
      var nodes = Array.prototype.filter.call(dialog.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null;
      });
      if (!nodes.length) { event.preventDefault(); return; }
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      var active = document.activeElement;
      if (event.shiftKey && (active === first || !dialog.contains(active) || active === dialog)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && (active === last || !dialog.contains(active))) {
        event.preventDefault(); first.focus();
      }
    }

    function markSubscribed(email) {
      writeStore(cfg.storageKey, { status: 'subscribed', at: Date.now() });
      if (typeof cfg.onSubscribe === 'function') cfg.onSubscribe(email);
    }

    form.addEventListener('submit', function (event) {
      var email = (input.value || '').trim();
      if (!EMAIL_RE.test(email)) {
        event.preventDefault();
        showError(cfg.copy.invalid);
        input.focus();
        return;
      }
      clearError();
      input.value = email;

      if (typeof cfg.submit !== 'function') {
        // Native post (Shopify). Remember the address was sent so the reload can be reported accurately.
        setLoading(true);
        return;
      }

      event.preventDefault();
      setLoading(true);
      Promise.resolve()
        .then(function () { return cfg.submit(email); })
        .then(function () {
          setLoading(false);
          markSubscribed(email);
          setState('success');
          focusInitial();
        })
        .catch(function (err) {
          setLoading(false);
          showError((err && err.message) || cfg.copy.failed);
          input.focus();
        });
    });

    input.addEventListener('input', function () { if (!errorEl.hidden) clearError(); });

    Array.prototype.forEach.call(root.querySelectorAll('[data-kp-close]'), function (el) {
      el.addEventListener('click', function () { close(el.getAttribute('data-kp-close') || 'close'); });
    });

    if (copyBtn && codeEl) {
      copyBtn.addEventListener('click', function () {
        var code = (codeEl.textContent || '').trim();
        var finish = function () {
          copyBtn.textContent = cfg.copy.copied;
          if (live) live.textContent = cfg.copy.copiedLive;
          window.setTimeout(function () { copyBtn.textContent = copyLabel; if (live) live.textContent = ''; }, 2000);
          if (typeof cfg.onCopy === 'function') cfg.onCopy(code);
        };
        var fallback = function () {
          var range = document.createRange();
          range.selectNodeContents(codeEl);
          var sel = window.getSelection();
          sel.removeAllRanges(); sel.addRange(range);
          try { document.execCommand('copy'); } catch (e) { /* leave the code selected so it can be copied by hand */ }
          finish();
        };
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(finish, fallback);
        } else {
          fallback();
        }
      });
    }

    // ---- Decide whether to open on this page view ----
    var serverPosted = root.querySelector('[data-kp-posted]');
    var serverError = root.querySelector('[data-kp-server-error]');
    var stored = readStore(cfg.storageKey);
    var DAY = 24 * 60 * 60 * 1000;

    // If the page has another customer form (the footer newsletter), Shopify can mark both as posted.
    // The URL hash names the form that was actually submitted, so only react to our own.
    if (serverPosted && form.id && window.location.hash && window.location.hash !== '#' + form.id) {
      serverPosted = null;
      serverError = null;
    }

    if (serverPosted) {
      // Shopify accepted the signup on the previous request.
      markSubscribed(input.value || '');
      open('success');
    } else if (serverError && serverError.textContent.trim()) {
      open('signup');
      showError(serverError.textContent.trim());
    } else {
      var quiet = stored && (
        (stored.status === 'subscribed' && Date.now() - stored.at < cfg.subscribedDays * DAY) ||
        (stored.status === 'dismissed' && Date.now() - stored.at < cfg.dismissDays * DAY)
      );
      if (!quiet && cfg.delayMs >= 0) {
        preload('signup');
        openTimer = window.setTimeout(function () { open('signup'); }, cfg.delayMs);
      }
    }

    return { open: open, close: close, setState: setState, el: root };
  }

  window.KudsiPopup = { init: init };
})();
